
import React, { useState, useMemo, useCallback } from 'react';
import { Button } from './Button';
import { AppSettings, BuybackDeviceType, BuybackCondition, BuybackAccessory, BuybackRequest, DeviceModelInfo, ConditionQuestion, BuybackPricingFactors, Employee } from '../types';
import { BUYBACK_DEVICE_TYPES, BUYBACK_CONDITIONS, BUYBACK_CONDITION_QUESTIONS, BUYBACK_ACCESSORIES_LIST } from '../constants';

interface BuybackWidgetPageProps {
  appSettings: AppSettings;
  onSaveRequest: (requestData: Omit<BuybackRequest, 'id' | 'createdAt' | 'status'>) => string;
  onGoHome: () => void;
  initialDeviceType?: BuybackDeviceType;
  initialModelId?: string;
  initialCondition?: BuybackCondition;
  buybackDeviceModels: Record<BuybackDeviceType, DeviceModelInfo[]>;
  buybackPricingFactors: BuybackPricingFactors;
  onCompleteBuybackAndAddToInventory: (buybackRequestId: string, finalValue: number, adminNotes?: string) => void;
  currentUser: Employee | null;
}

interface FormData {
  customerName: string;
  customerEmail: string;
  customerPhone: string;
  deviceType: BuybackDeviceType | '';
  deviceModel: string; // Model ID
  storageCapacity: string;
  imeiOrSerial: string;
  overallCondition: BuybackCondition | '';
  conditionAnswers: Record<string, boolean>;
  includedAccessories: BuybackAccessory[];
  additionalNotes: string;
  agreeToTerms: boolean;
}

const HomeIcon: React.FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5 mr-2" {...props}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 12l8.954-8.955a1.125 1.125 0 011.591 0L21.75 12M4.5 9.75v10.125c0 .621.504 1.125 1.125 1.125H9.75v-4.875c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21h4.125c.621 0 1.125-.504 1.125-1.125V9.75M8.25 21h7.5" />
  </svg>
);

const BUYBACK_AGREEMENT_TEXT = `I confirm that I am the legal owner of the device described above and have the right to sell it. I understand that the initial estimate is subject to verification by Tech Medics upon physical inspection of the device. If the device's condition differs from what was described, Tech Medics may offer a revised buyback value or decline the buyback. I have removed all personal data and disabled any account locks (e.g., Find My iPhone/Android, Google Lock) before submitting this device. Tech Medics is not responsible for data left on the device.`;


export const BuybackWidgetPage: React.FC<BuybackWidgetPageProps> = ({
    appSettings,
    onSaveRequest,
    onGoHome,
    initialDeviceType,
    initialModelId,
    initialCondition,
    buybackDeviceModels,
    buybackPricingFactors,
    onCompleteBuybackAndAddToInventory,
    currentUser,
}) => {

  const getInitialFormData = useCallback((): FormData => {
    const defaultConditionAnswers = BUYBACK_CONDITION_QUESTIONS.reduce((acc, q) => ({ ...acc, [q.id]: true }), {});
    return {
        customerName: '',
        customerEmail: '',
        customerPhone: '',
        deviceType: initialDeviceType || '',
        deviceModel: initialModelId || '',
        storageCapacity: '',
        imeiOrSerial: '',
        overallCondition: initialCondition || '',
        conditionAnswers: defaultConditionAnswers,
        includedAccessories: [],
        additionalNotes: '',
        agreeToTerms: false,
    };
  },[initialDeviceType, initialModelId, initialCondition]);


  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState<FormData>(getInitialFormData());
  const [errors, setErrors] = useState<Partial<Record<keyof FormData | 'conditionAnswers', string>>>({});
  const [submittedRequestId, setSubmittedRequestId] = useState<string | null>(null);
  const [submittedEstimatedValue, setSubmittedEstimatedValue] = useState<number>(0);

  // Admin Modal State
  const [isAdminModalOpen, setIsAdminModalOpen] = useState(false);
  const [finalAgreedValue, setFinalAgreedValue] = useState<number>(0);
  const [adminInventoryNotes, setAdminInventoryNotes] = useState('');


  const availableModels = useMemo(() => {
    return formData.deviceType ? (buybackDeviceModels[formData.deviceType] || []) : [];
  }, [formData.deviceType, buybackDeviceModels]);

  const selectedModelInfo = useMemo(() => {
    return availableModels.find(m => m.id === formData.deviceModel);
  }, [availableModels, formData.deviceModel]);

  const estimatedValue = useMemo(() => {
    if (!selectedModelInfo || !formData.overallCondition) return 0;

    let value = selectedModelInfo.baseValue;
    value *= buybackPricingFactors.conditionMultiplier[formData.overallCondition];

    BUYBACK_CONDITION_QUESTIONS.forEach(q => {
      const userAnswer = formData.conditionAnswers[q.id];
      if (q.positiveImpact && !userAnswer) {
        value -= selectedModelInfo.baseValue * q.valueAdjustmentFactor;
      } else if (!q.positiveImpact && userAnswer) {
        value -= selectedModelInfo.baseValue * q.valueAdjustmentFactor;
      }
    });

    if (formData.storageCapacity && selectedModelInfo.storageOptions) {
        const storageFactorKey = formData.storageCapacity as keyof typeof buybackPricingFactors.storageBonusPercentage;
        const storageFactor = buybackPricingFactors.storageBonusPercentage[storageFactorKey] || 0;
        value += selectedModelInfo.baseValue * storageFactor;
    }

    formData.includedAccessories.forEach(acc => {
        const accessoryValueKey = acc as keyof typeof buybackPricingFactors.accessoryValue;
        value += buybackPricingFactors.accessoryValue[accessoryValueKey] || 0;
    });

    if (formData.overallCondition === BuybackCondition.POOR_BROKEN) {
        value = Math.max(value, buybackPricingFactors.MINIMUM_POOR_VALUE);
    }

    return Math.max(0, parseFloat(value.toFixed(2)));
  }, [formData, selectedModelInfo, buybackPricingFactors]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;

    if (type === 'checkbox') {
      const checkboxElement = e.target as HTMLInputElement;
      const { name: checkboxName, checked, value: checkboxValue } = checkboxElement;

      if (checkboxName === 'agreeToTerms') {
        setFormData(prev => ({ ...prev, agreeToTerms: checked }));
      } else if (BUYBACK_CONDITION_QUESTIONS.some(q => q.id === checkboxName)) {
        setFormData(prev => ({
            ...prev,
            conditionAnswers: { ...prev.conditionAnswers, [checkboxName]: checked }
        }));
      } else if (checkboxName === 'includedAccessories') {
        const accessory = checkboxValue as BuybackAccessory;
        setFormData(prev => ({
          ...prev,
          includedAccessories: checked
            ? [...prev.includedAccessories, accessory]
            : prev.includedAccessories.filter(acc => acc !== accessory)
        }));
      }
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
      if (name === 'deviceType') {
        setFormData(prevData => ({ ...prevData, deviceModel: '', storageCapacity: ''}));
      }
      if (name === 'deviceModel') {
        setFormData(prevData => ({ ...prevData, storageCapacity: ''}));
      }
    }

    if (errors[name as keyof FormData]) {
      setErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };

  const validateStep = () => {
    const newErrors: Partial<Record<keyof FormData | 'conditionAnswers', string>> = {};
    if (currentStep === 1) {
      if (!formData.deviceType) newErrors.deviceType = 'Device type is required.';
      if (!formData.deviceModel) newErrors.deviceModel = 'Device model is required.';
    } else if (currentStep === 2) {
      if (!formData.overallCondition) newErrors.overallCondition = 'Overall condition is required.';
    } else if (currentStep === 4) {
      if (!formData.customerName.trim()) newErrors.customerName = 'Your name is required.';
      if (!formData.customerEmail.trim()) newErrors.customerEmail = 'Your email is required.';
      else if (!/^\S+@\S+\.\S+$/.test(formData.customerEmail)) newErrors.customerEmail = 'Invalid email format.';
      if (!formData.agreeToTerms) newErrors.agreeToTerms = 'You must agree to the terms.';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNextStep = () => {
    if (validateStep()) {
      setCurrentStep(prev => prev + 1);
      window.scrollTo(0,0);
    }
  };

  const handlePrevStep = () => {
    setCurrentStep(prev => prev - 1);
    window.scrollTo(0,0);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateStep()) {
      const finalEstimatedValue = estimatedValue;
      const requestData: Omit<BuybackRequest, 'id' | 'createdAt' | 'status'> = {
        customerName: formData.customerName,
        customerEmail: formData.customerEmail,
        customerPhone: formData.customerPhone,
        deviceType: formData.deviceType as BuybackDeviceType,
        deviceModel: selectedModelInfo?.name || 'Unknown Model',
        storageCapacity: formData.storageCapacity,
        imeiOrSerial: formData.imeiOrSerial,
        overallCondition: formData.overallCondition as BuybackCondition,
        powersOn: formData.conditionAnswers['powersOn'],
        screenIntact: formData.conditionAnswers['screenIntact'],
        noMajorScratchesDents: formData.conditionAnswers['noMajorScratchesDents'],
        buttonsPortsFunctional: formData.conditionAnswers['buttonsPortsFunctional'],
        noWaterDamage: formData.conditionAnswers['noWaterDamage'],
        notAccountLocked: formData.conditionAnswers['notAccountLocked'],
        includedAccessories: formData.includedAccessories,
        additionalNotes: formData.additionalNotes,
        estimatedValue: finalEstimatedValue,
      };
      const newId = onSaveRequest(requestData);
      setSubmittedRequestId(newId);
      setSubmittedEstimatedValue(finalEstimatedValue);
      setFormData(getInitialFormData());
      setCurrentStep(5); // Go to success screen
      window.scrollTo(0,0);
    }
  };

  const handleOpenAdminModal = () => {
    setFinalAgreedValue(submittedEstimatedValue);
    setAdminInventoryNotes('');
    setIsAdminModalOpen(true);
  };

  const handleAdminModalSubmit = () => {
    if (!submittedRequestId) {
      alert("Error: No submitted buyback request ID found.");
      setIsAdminModalOpen(false);
      return;
    }
    if (finalAgreedValue <= 0) {
      alert("Please enter a valid final agreed value.");
      return;
    }
    onCompleteBuybackAndAddToInventory(submittedRequestId, finalAgreedValue, adminInventoryNotes);
    setIsAdminModalOpen(false);
  };

  const renderError = (field: keyof FormData | 'conditionAnswers') => {
    return errors[field] ? <p className="text-red-500 text-xs mt-1">{errors[field]}</p> : null;
  };

  const renderStep = () => {
    switch (currentStep) {
      case 1: // Device Selection
        return (
          <div className="space-y-5">
            <div>
              <label htmlFor="deviceType" className="block text-sm font-medium text-slate-700 mb-1">Select Device Type <span className="text-red-500">*</span></label>
              <select name="deviceType" id="deviceType" value={formData.deviceType} onChange={handleChange} className="w-full form-input">
                <option value="">-- Select Type --</option>
                {BUYBACK_DEVICE_TYPES.map(type => <option key={type} value={type}>{type}</option>)}
              </select>
              {renderError('deviceType')}
            </div>
            {formData.deviceType && (
              <div>
                <label htmlFor="deviceModel" className="block text-sm font-medium text-slate-700 mb-1">Select Model <span className="text-red-500">*</span></label>
                <select name="deviceModel" id="deviceModel" value={formData.deviceModel} onChange={handleChange} className="w-full form-input" disabled={availableModels.length === 0}>
                  <option value="">-- Select Model --</option>
                  {availableModels.map(model => <option key={model.id} value={model.id}>{model.name}</option>)}
                </select>
                {renderError('deviceModel')}
              </div>
            )}
            {selectedModelInfo?.storageOptions && selectedModelInfo.storageOptions.length > 0 && (
              <div>
                <label htmlFor="storageCapacity" className="block text-sm font-medium text-slate-700 mb-1">Storage Capacity / Specifics</label>
                <select name="storageCapacity" id="storageCapacity" value={formData.storageCapacity} onChange={handleChange} className="w-full form-input">
                  <option value="">-- Select Storage (if applicable) --</option>
                  {selectedModelInfo.storageOptions.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                </select>
              </div>
            )}
          </div>
        );
      case 2: // Condition
        return (
          <div className="space-y-5">
            <div>
              <label htmlFor="overallCondition" className="block text-sm font-medium text-slate-700 mb-1">Overall Condition <span className="text-red-500">*</span></label>
              <select name="overallCondition" id="overallCondition" value={formData.overallCondition} onChange={handleChange} className="w-full form-input">
                <option value="">-- Select Condition --</option>
                {BUYBACK_CONDITIONS.map(cond => <option key={cond} value={cond}>{cond}</option>)}
              </select>
              {renderError('overallCondition')}
            </div>
            <fieldset className="mt-4">
              <legend className="text-sm font-medium text-slate-700 mb-2">Detailed Condition Questions:</legend>
              <div className="space-y-3">
                {BUYBACK_CONDITION_QUESTIONS.map(question => (
                  <label key={question.id} htmlFor={question.id} className="flex items-center p-3 border border-slate-200 rounded-md hover:bg-slate-50 cursor-pointer has-[:checked]:bg-sky-50 has-[:checked]:border-sky-300">
                    <input
                      type="checkbox"
                      id={question.id}
                      name={question.id}
                      checked={formData.conditionAnswers[question.id] || false}
                      onChange={handleChange}
                      className="h-4 w-4 text-[var(--color-primary)] border-slate-300 rounded focus:ring-[var(--color-primary)]"
                    />
                    <span className="ml-3 text-sm text-slate-700">{question.label}</span>
                  </label>
                ))}
              </div>
            </fieldset>
          </div>
        );
      case 3: // Accessories & Estimate
        return (
          <div className="space-y-5">
            <div>
              <label htmlFor="imeiOrSerial" className="block text-sm font-medium text-slate-700 mb-1">IMEI or Serial Number (Optional but Recommended)</label>
              <input type="text" name="imeiOrSerial" id="imeiOrSerial" value={formData.imeiOrSerial} onChange={handleChange} className="w-full form-input" />
            </div>
            <fieldset>
              <legend className="text-sm font-medium text-slate-700 mb-2">Included Accessories (Optional):</legend>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2">
                {BUYBACK_ACCESSORIES_LIST.map(accessory => (
                  <label key={accessory} htmlFor={`accessory-${accessory}`} className="flex items-center p-2 border border-slate-200 rounded-md hover:bg-slate-50 cursor-pointer has-[:checked]:bg-sky-50 has-[:checked]:border-sky-300">
                    <input
                      type="checkbox"
                      id={`accessory-${accessory}`}
                      name="includedAccessories"
                      value={accessory}
                      checked={formData.includedAccessories.includes(accessory)}
                      onChange={handleChange}
                      className="h-4 w-4 text-[var(--color-primary)] border-slate-300 rounded focus:ring-[var(--color-primary)]"
                    />
                    <span className="ml-3 text-sm text-slate-700">{accessory}</span>
                  </label>
                ))}
              </div>
            </fieldset>
            <div className="p-4 bg-slate-100 rounded-lg text-center shadow-inner">
              <p className="text-md text-slate-600 mb-1">Estimated Buyback Value:</p>
              <p className="text-3xl font-bold text-[var(--color-primary)]">${estimatedValue.toFixed(2)}</p>
              <p className="text-xs text-slate-500 mt-1">Final value subject to verification.</p>
            </div>
          </div>
        );
      case 4: // Customer Info & Agreement
        return (
          <div className="space-y-5">
            <div>
              <label htmlFor="customerName" className="block text-sm font-medium text-slate-700 mb-1">Your Full Name <span className="text-red-500">*</span></label>
              <input type="text" name="customerName" id="customerName" value={formData.customerName} onChange={handleChange} className="w-full form-input" required />
              {renderError('customerName')}
            </div>
            <div>
              <label htmlFor="customerEmail" className="block text-sm font-medium text-slate-700 mb-1">Your Email Address <span className="text-red-500">*</span></label>
              <input type="email" name="customerEmail" id="customerEmail" value={formData.customerEmail} onChange={handleChange} className="w-full form-input" required />
              {renderError('customerEmail')}
            </div>
            <div>
              <label htmlFor="customerPhone" className="block text-sm font-medium text-slate-700 mb-1">Your Phone Number (Optional)</label>
              <input type="tel" name="customerPhone" id="customerPhone" value={formData.customerPhone} onChange={handleChange} className="w-full form-input" />
            </div>
            <div>
              <label htmlFor="additionalNotes" className="block text-sm font-medium text-slate-700 mb-1">Additional Notes (Optional)</label>
              <textarea name="additionalNotes" id="additionalNotes" value={formData.additionalNotes} onChange={handleChange} rows={3} className="w-full form-input"></textarea>
            </div>
            <div className="space-y-2 pt-2">
              <h3 className="text-sm font-semibold text-slate-700">Agreement Terms</h3>
              <div className="h-24 overflow-y-auto p-3 border border-slate-200 rounded-md bg-slate-50 text-xs text-slate-600">
                {BUYBACK_AGREEMENT_TEXT}
              </div>
              <div className="flex items-start">
                <input type="checkbox" name="agreeToTerms" id="agreeToTerms" checked={formData.agreeToTerms} onChange={handleChange} className="h-5 w-5 text-[var(--color-primary)] border-slate-300 rounded focus:ring-[var(--color-primary)] mt-0.5" required />
                <label htmlFor="agreeToTerms" className="ml-2.5 block text-sm text-slate-700">
                  I have read and agree to the terms and conditions outlined above. <span className="text-red-500">*</span>
                </label>
              </div>
              {renderError('agreeToTerms')}
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  const progressPercentage = ((currentStep -1) / 4) * 100; // 4 steps before success

  return (
    <div className="min-h-screen bg-slate-100 flex flex-col items-center justify-center p-4 sm:p-6 relative">
      <Button
        variant="ghost"
        onClick={onGoHome}
        className="absolute top-4 right-4 text-slate-600 hover:text-[var(--color-primary)] hover:bg-[var(--color-primary)] hover:bg-opacity-10 !px-3 !py-2 flex items-center text-sm z-10"
        aria-label="Go to Dashboard / Home"
      >
        <HomeIcon />
        Exit Buyback Tool
      </Button>

      <div className="bg-white p-6 sm:p-10 rounded-xl shadow-2xl w-full max-w-2xl">
        <div className="text-center mb-8">
          {appSettings.logoBase64 ? (
            <img src={appSettings.logoBase64} alt={`${appSettings.companyName} Logo`} className="mx-auto h-16 mb-4 object-contain" />
          ) : (
            <h1 className="text-3xl sm:text-4xl font-bold text-[var(--color-primary)]">{appSettings.companyName}</h1>
          )}
          <p className="text-lg text-slate-600 mt-1">Device Buyback Quote Tool</p>
        </div>

        {currentStep < 5 && (
            <div className="mb-6">
                <div className="w-full bg-slate-200 rounded-full h-2.5">
                    <div className="bg-[var(--color-primary)] h-2.5 rounded-full transition-all duration-500 ease-out" style={{ width: `${progressPercentage}%` }}></div>
                </div>
                <p className="text-xs text-slate-500 text-center mt-1">Step {currentStep} of 4</p>
            </div>
        )}

        {currentStep < 5 && (
          <form onSubmit={handleSubmit} className="space-y-6">
            {renderStep()}
            <div className="flex justify-between items-center pt-5 border-t border-slate-200">
              <Button type="button" variant="ghost" onClick={handlePrevStep} disabled={currentStep === 1}>
                Previous
              </Button>
              {currentStep < 4 ? (
                <Button type="button" variant="primary" onClick={handleNextStep}>
                  Next
                </Button>
              ) : (
                <Button type="submit" variant="primary" className="bg-emerald-600 hover:bg-emerald-700 focus:ring-emerald-500">
                  Submit Buyback Request
                </Button>
              )}
            </div>
          </form>
        )}

        {currentStep === 5 && submittedRequestId && (
          <div className="text-center py-8">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="var(--color-accent)" className="w-20 h-20 mx-auto mb-6">
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <h2 className="text-3xl font-bold text-slate-800 mb-4">Request Submitted!</h2>
            <p className="text-lg text-slate-600 mb-2">Thank you for submitting your buyback request.</p>
            <p className="text-md text-slate-600">Your Request ID is: <span className="font-semibold text-[var(--color-primary)]">{submittedRequestId}</span></p>
            <p className="text-xl font-semibold text-slate-700 my-3">Estimated Value: <span className="text-[var(--color-accent)]">${submittedEstimatedValue.toFixed(2)}</span></p>
            <p className="text-sm text-slate-500 mb-6">A team member will review your request and contact you soon with a final offer or next steps. Please bring your device and this Request ID to the store.</p>
            
            {currentUser?.role === 'Administrator' && (
                <div className="mt-8 pt-6 border-t border-slate-300">
                <h3 className="text-lg font-semibold text-slate-700 mb-3">Admin Actions:</h3>
                <Button
                    variant="outline"
                    onClick={handleOpenAdminModal}
                    className="!text-[var(--color-primary)] border-[var(--color-primary)] hover:!bg-[var(--color-primary)] hover:!bg-opacity-10"
                >
                    Complete Trade-in & Add to Inventory
                </Button>
                </div>
            )}

            <Button variant="secondary" onClick={() => { setCurrentStep(1); setSubmittedRequestId(null); }} className="mt-8 text-lg px-8 py-3">
              Start New Buyback Quote
            </Button>
          </div>
        )}
      </div>

      {isAdminModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[150]">
          <div className="bg-white p-6 rounded-lg shadow-xl w-full max-w-md">
            <h2 className="text-xl font-semibold text-slate-800 mb-4">Finalize Trade-in & Add to Inventory</h2>
            <p className="text-sm text-slate-600 mb-2">Request ID: <span className="font-medium">{submittedRequestId}</span></p>
            <p className="text-sm text-slate-600 mb-4">Original Estimate: <span className="font-medium">${submittedEstimatedValue.toFixed(2)}</span></p>
            
            <div className="mb-4">
              <label htmlFor="finalAgreedValue" className="block text-sm font-medium text-slate-700 mb-1">Final Agreed Value <span className="text-red-500">*</span></label>
              <input
                type="number"
                id="finalAgreedValue"
                value={finalAgreedValue}
                onChange={(e) => setFinalAgreedValue(parseFloat(e.target.value) || 0)}
                className="form-input w-full"
                step="0.01"
                min="0"
              />
            </div>

            <div className="mb-4">
              <label htmlFor="adminInventoryNotes" className="block text-sm font-medium text-slate-700 mb-1">Admin Notes for Inventory (Optional)</label>
              <textarea
                id="adminInventoryNotes"
                value={adminInventoryNotes}
                onChange={(e) => setAdminInventoryNotes(e.target.value)}
                rows={3}
                className="form-input w-full"
                placeholder="e.g., Minor scratch on back, customer agreed to reduced value."
              />
            </div>

            <div className="flex justify-end space-x-3">
              <Button variant="ghost" onClick={() => setIsAdminModalOpen(false)}>Cancel</Button>
              <Button variant="primary" onClick={handleAdminModalSubmit}>Confirm & Add to Inventory</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
